<template>
  <div class="d-flex flex-column fill-height">
    <div class="vacancy-questions__header-block">
      <div v-if="transcriptions.length" class="link" @click="goToReview">
        {{ $t('viewSummary') }}
      </div>

      <div v-if="!areQuestionsEqual">
        <Tooltip :position="'left'" :position-mob="'right'" :max-width="300">
          <template #trigger>
            <VIcon>
              <template>
                {{ mdiHelpCircle }}
              </template>
            </VIcon>
          </template>
          <template #content>
            {{ $t('questionsChanged') }}
          </template>
        </Tooltip>
      </div>
    </div>

    <VList
      rounded
      class="vacancy-questions__list pa-2 pa-md-4 flex-grow-1"
      :class="{'huntica-nice-scroll': $vuetify.breakpoint.smAndUp}"
    >
      <VListItemGroup v-model="currentQuestion" mandatory>
        <VListItem
          v-for="(q, index) in filteredOnlineInterviewTimestamps"
          :key="`${q.questionType}-${q.questionId}`"
          active-class="item-active"
          class="vacancy-questions__item"
          :class="{ 'pre-active': currentQuestion - 1 === index }"
        >
          <div class="vacancy-questions__item_header">
            <VListItemIcon class="vacancy-questions__icon mr-2">
              <VIcon
                :color="'primary'"
                :disabled="isDisabledQuestion(q.questionId, q.questionType)"
                @click="togglePlayPause(index)"
              >
                <template v-if="currentQuestion === index && isQuestionPlaying">
                  {{ mdiPause }}
                </template>
                <template v-else>
                  {{ mdiPlay }}
                </template>
              </VIcon>
            </VListItemIcon>

            <VListItemIcon
              v-if="shouldShowTranscriptionIcon"
              class="vacancy-questions__icon mr-3"
            >
              <VIcon @click="toggleTxtIcon(index)">
                <template v-if="isTxtTextIconsClicked[index]">
                  {{ mdiArrowUp }}
                </template>
                <template v-else>
                  TXT
                </template>
              </VIcon>
            </VListItemIcon>

            <VListItemContent class="body-2">
              <div class="d-flex justify-start">
                <span class="num pr-3">{{ index + 1 }}.</span>
                <span class="text">{{ q.questionText }}</span>
              </div>
            </VListItemContent>
          </div>

          <div v-if="isTxtTextIconsClicked[index]" class="vacancy-questions__item_content">
            <template v-if="getTranscriptionByQuestionId(q.questionId)">
              <div class="vacancy-questions__item_content-text" :class="{ maxHeight: isReadFull }">
                {{ getTranscriptionByQuestionId(q.questionId).transcription }}
              </div>
              <div
                v-if="whichQestionLength(getTranscriptionByQuestionId(q.questionId).transcription) >= 190"
                class="read-full"
                @click="readFull"
              >
                <template v-if="!isReadFull">
                  {{ $t('readFull') }}
                </template>
                <template v-else>
                  {{ $t('hide') }}
                </template>
              </div>
            </template>
          </div>
        </VListItem>
      </VListItemGroup>
    </VList>
  </div>
</template>

<script>
import {
  mdiPause, mdiPlay, mdiArrowUp, mdiHelpCircle,
} from '@mdi/js';
import {
  computed, ref, toRefs,
} from '@vue/composition-api';
import Tooltip from '../common/Tooltip.vue';
import { timestampTypes } from '../../enums';

export default {
  name: 'VacancyQuestionsList',
  components: { Tooltip },
  model: {
    prop: 'question',
    event: 'input',
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    question: {
      type: Number,
      default: 0,
    },
    onlineInterview: {
      type: Object,
      required: true,
    },
    areQuestionsEqual: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { onlineInterview } = toRefs(props);
    const isReadFull = ref(false);

    const transcriptions = computed(() => onlineInterview.value?.timestamps.filter(i => i.transcription));

    const filteredOnlineInterviewTimestamps = computed(() => (onlineInterview.value?.timestamps || []).filter(
      t => t.type === timestampTypes.ANSWERING,
    ));

    const shouldShowTranscriptionIcon = computed(() => transcriptions.value.length > 0);

    function goToReview() {
      emit('change-active-tab', 0);
    }

    function readFull() {
      isReadFull.value = !isReadFull.value;
    }

    function whichQestionLength(text) {
      return text.split(' ').join('').length;
    }

    function getTranscriptionByQuestionId(questionId) {
      return transcriptions.value.find(t => t.questionId === questionId && t.transcription);
    }

    return {
      isReadFull,
      readFull,
      transcriptions,
      whichQestionLength,
      goToReview,
      getTranscriptionByQuestionId,
      filteredOnlineInterviewTimestamps,
      shouldShowTranscriptionIcon,
    };
  },
  data() {
    return {
      isQuestionPlaying: false,
      isTxtTextIcon: false,
      isTxtTextIconsClicked: [],
      mdiPause,
      mdiPlay,
      mdiArrowUp,
      mdiHelpCircle,
    };
  },
  computed: {
    toggleVideoPlayerEvent() {
      return new CustomEvent('toggleVideoPlayerPlayPause');
    },
    currentQuestion: {
      get() {
        return this.question;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  mounted() {
    document.addEventListener('toggleQuestionListPlayPause', this.toggleQuestionListPlayPause);
  },
  beforeDestroy() {
    document.removeEventListener('toggleQuestionListPlayPause', this.toggleQuestionListPlayPause);
  },
  methods: {
    isDisabledQuestion(id, type) {
      return !this.onlineInterview.timestamps?.some(
        t => t.questionId === id && t.questionType === type);
    },
    togglePlayPause(index) {
      if (index !== this.currentQuestion && this.isQuestionPlaying) {
        return;
      }
      this.toggleQuestionListPlayPause();
      this.toggleVideoPlayerPlayPause();
    },
    togglePlayFromOutside(question) {
      if (question !== this.currentQuestion && this.isQuestionPlaying) {
        return;
      }

      this.toggleQuestionListPlayPause();
      this.toggleVideoPlayerPlayPause();
    },
    toggleQuestionListPlayPause() {
      this.isQuestionPlaying = !this.isQuestionPlaying;
    },
    toggleVideoPlayerPlayPause() {
      document.dispatchEvent(this.toggleVideoPlayerEvent);
    },
    toggleTxtIcon(index) {
      this.$set(this.isTxtTextIconsClicked, index, !this.isTxtTextIconsClicked[index]);
    },
  },
};
</script>

<style lang="scss" scoped>
.vacancy-questions {
  &__list {
    background-color: transparent;
    overflow-y: auto;
    max-height: 100%;
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      height: 0;
    }
  }

  &__header-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px 20px 0;
    @media screen and (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      margin: 10px 20px 10px;
    }
  }

  &__item {
    background: #fff;
    border-radius: 10px !important;
    margin-bottom: 5px !important;
    position: relative;
    display: block;
    &_header {
      display: flex;
      align-items: center;
      > div:nth-child(2) {
      display: block;
        button {
          color: #000 !important;
          text-align: center;
          font-size: 10px;
          font-weight: 400;
          line-height: 100%;
          padding: 7px 3px;
        }
      }
    }

    &_content {
      padding-bottom: 20px;
    }
    &_content-text {
      max-height: 60px;
      overflow-y: hidden;
      color: #212121;
      font-size: 14px;
      font-weight: 300;
      line-height: 150%;
    }

    .num {
      font-size: 15px;
    }
  }

  &__icon {
    cursor: pointer;
    border: 1px solid #E0E0E0;
    border-radius: 50%;
    button {
      width: 26px;
      height: 26px;
    }
    &:nth-child(2) button{
      font-size: 10px;
    }
  }
}
.summary {
  color: #0A82D9;
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  text-decoration: underline;
}

.heigth {
  height: 300px;
}

.maxHeight {
  max-height: 100% !important;
  transition: .2s;
}

.read-full {
  bottom: 15px;
  width: 100%;
  text-align: center;
  color: #0A82D9;
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  text-decoration-line: underline;
  cursor: pointer;
  margin-top: 20px;
}

.v-icon.v-icon::after {
  display: none;
}

.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none;
}
.v-card::v-deep {
  background-color: #F5F5F5;
  border-radius: 10px;
}
.v-list-item::after {
  display: none;
}
.v-list-item__subtitle {
  flex: auto;
  overflow: inherit;
}

.error {
  &__title {
    color: #212121;
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
  }
  &__trial {
    background-color: #FFECEC;
    padding-left: 68px !important;
    @media screen and (max-width: 600px) {
      padding: 20px 40px !important;
    }
    &_icon {
      position: absolute;
      top: 10px;
      left: 10px;
      @media screen and (max-width: 600px) {
        position: static;
        margin-bottom: 15px;
      }
    }
    &_links {
      display: flex;
      align-items: center;
      @media screen and (max-width: 1265px) {
        margin-top: 10px;
      }
      @media screen and (max-width: 600px) {
        justify-content: space-between;
        margin-top: 20px;
      }
    }
    .row {
      display: flex;
      .try-days-block {
        margin-right: 25px;
      }
      .change-tariff-block {
        white-space: nowrap;
      }
    }

    .button-trial {
      color: #0A82D9;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }

    .button-tariff {
      color: #212121;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }
  }
}
.v-list-item--link:before {
  display: none;
}

.link {
  font-size: 13px;
  color: #0a82d9;
  text-decoration: underline;
  cursor: pointer;
}

.item-active {
  border: 1px solid #1F85D7;
}
</style>

<i18n lang="yml" locale="en">
  conversionIsAvailable: Conversion is available at the "Standard" tariff and above
  free: 30 days for free
  changeTariff: Change your tariff
  payTheTariff: Pay the tariff
  inProcessing: Still in processing!
  readFull: Read completely
  summaryAvailable: A brief transcript of the interview from AI is available
  hide: Hide
  noAnswer: No answer
  viewSummary: View the interview summary from AI
  questionsChanged: The job questions are different from the questions the candidate answered.
</i18n>

<i18n lang="yml" locale="ru">
  conversionIsAvailable: Конвертация доступна на тарифе «Стандарт» и выше
  free: Бесплатно 30 дн.
  changeTariff: Сменить тариф
  payTheTariff: Оплатить тариф
  inProcessing: Еще в обработке!
  readFull: Читать полностью
  summaryAvailable: Вам доступно краткое содержание собеседования
  hide: Скрыть
  noAnswer: Нет ответа
  viewSummary: Посмотреть резюме собеседования от ИИ
  questionsChanged: Вопросы вакансии отличаются от вопросов, на которые отвечал кандидат.
</i18n>
